import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  policyDocumentApi,
  PolicyDocumentCompletion,
  PolicyDocumentDetail,
  PolicyDocumentRecipientRow,
} from '../services/api';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import { triggerBlobDownload } from '../utils/certificateFiles';
import { appConfirm } from '../context/SystemDialogContext';

const ACK_FILTERS = [
  { id: 'all', label: 'All', countKey: 'required' as const },
  { id: 'acknowledged', label: 'Acknowledged', countKey: 'complete' as const },
  { id: 'opened', label: 'Opened', countKey: 'opened' as const },
  { id: 'notopened', label: 'Not opened', countKey: 'notOpened' as const },
  { id: 'outstanding', label: 'Outstanding', countKey: 'outstanding' as const },
] as const;

const DocumentPolicyDetailPage: React.FC = () => {
  const { id } = useParams();
  const docId = Number(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'acknowledgements' ? 'acknowledgements' : searchParams.get('tab') === 'versions' ? 'versions' : 'details';

  const [doc, setDoc] = useState<PolicyDocumentDetail | null>(null);
  const [filter, setFilter] = useState('all');
  const [acks, setAcks] = useState<PolicyDocumentRecipientRow[]>([]);
  const [ackSummary, setAckSummary] = useState<PolicyDocumentCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newVersionLabel, setNewVersionLabel] = useState('');
  const [requireNewAck, setRequireNewAck] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async (cancelled?: () => boolean) => {
    if (!docId) return;
    setLoading(true);
    try {
      const detail = await policyDocumentApi.get(docId);
      if (cancelled?.()) return;
      setDoc(detail);
      setAckSummary(detail.completion);
      if (!newVersionLabel) setNewVersionLabel(detail.versionLabel || '1.0');
      if (tab === 'acknowledgements') {
        const ackData = await policyDocumentApi.acknowledgements(docId, filter === 'all' ? undefined : filter);
        if (cancelled?.()) return;
        setAcks(ackData.items || []);
        if (ackData.summary) setAckSummary(ackData.summary);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  useEffect(() => {
    if (!docId || tab !== 'acknowledgements') return;
    let cancelled = false;
    (async () => {
      try {
        const ackData = await policyDocumentApi.acknowledgements(docId, filter === 'all' ? undefined : filter);
        if (cancelled) return;
        setAcks(ackData.items || []);
        if (ackData.summary) setAckSummary(ackData.summary);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId, tab, filter]);

  const summary = ackSummary ?? doc?.completion;
  const isPdf = useMemo(() => (doc?.contentType || '').includes('pdf') || (doc?.fileName || '').toLowerCase().endsWith('.pdf'), [doc]);

  if (loading && !doc) {
    return <div className="p-6 text-neutral-500">Loading…</div>;
  }
  if (!doc) {
    return <div className="p-6 text-neutral-500">Document not found.</div>;
  }

  const setTab = (next: string) => {
    setSearchParams(next === 'details' ? {} : { tab: next });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <Link to="/documents-policies" className="text-sm text-blue-700 hover:underline">Documents & Policies</Link>
          <h1 className="text-2xl font-semibold text-neutral-900 mt-1">{doc.name}</h1>
          <p className="text-sm text-neutral-500">{doc.categoryLabel} · {doc.displayStatus}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => navigate(`/documents-policies/${doc.id}/edit`)} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm">Edit details</button>
          {doc.status === 'Draft' && (
            <button
              type="button"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                try {
                  const result = await policyDocumentApi.publish(doc.id);
                  window.alert(result.message);
                  await load();
                } catch (err: unknown) {
                  window.alert((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Publish failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {busy ? 'Publishing…' : 'Publish'}
            </button>
          )}
          {doc.status !== 'Archived' && (
            <button type="button" className="rounded-lg border border-rose-200 text-rose-700 px-3 py-2 text-sm" onClick={async () => {
              if (!(await appConfirm('Archive this document? Acknowledgement history is kept.', { danger: true, confirmLabel: 'Archive' }))) return;
              await policyDocumentApi.archive(doc.id);
              await load();
            }}>Archive</button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {(['details', 'acknowledgements', 'versions'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1 text-sm ${tab === t ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-700'}`}
          >
            {t === 'details' ? 'Details' : t === 'acknowledgements' ? 'Acknowledgements' : 'Versions'}
          </button>
        ))}
      </div>

      {tab === 'details' && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field label="Status" value={doc.displayStatus} />
          <Field label="Version" value={doc.versionLabel ? `v${doc.versionLabel}` : '—'} />
          <Field label="Category" value={doc.categoryLabel} />
          <Field label="Owner" value={doc.ownerLabel || '—'} />
          <Field label="Effective date" value={doc.effectiveDate ? formatCompanyDate(doc.effectiveDate) : '—'} />
          <Field label="Review date" value={doc.reviewDate ? formatCompanyDate(doc.reviewDate) : '—'} />
          <Field label="Audience" value={doc.audienceLabel} />
          <Field label="Acknowledgement" value={doc.requiresAcknowledgement ? `${summary?.complete ?? 0} / ${summary?.required ?? 0} (${summary?.percent ?? 0}%)` : 'Not required'} />
          <Field label="File" value={doc.fileName || 'None'} />
          <div className="sm:col-span-2 flex flex-col items-start gap-2 pt-2">
            {doc.fileName ? (
              <button
                type="button"
                className="rounded-lg bg-neutral-900 px-3 py-2 text-white text-sm"
                onClick={async () => {
                  try {
                    const blob = await policyDocumentApi.downloadFile(doc.id, undefined, isPdf ? 'inline' : 'attachment');
                    if (isPdf) {
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    } else {
                      triggerBlobDownload(blob, doc.fileName || 'document');
                    }
                  } catch {
                    window.alert('Could not open this document. Upload a PDF or Word file first.');
                  }
                }}
              >
                View document
              </button>
            ) : (
              <p className="text-sm text-neutral-500">
                No file uploaded yet. Use Edit details to add a PDF or Word file before publishing.
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'acknowledgements' && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Recipients" value={summary?.required ?? 0} />
            <Stat label="Acknowledged" value={summary?.complete ?? 0} />
            <Stat label="Outstanding" value={summary?.outstanding ?? 0} />
            <Stat label="Complete" value={`${summary?.percent ?? 0}%`} />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {ACK_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-label={f.label}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${filter === f.id ? 'bg-blue-600 text-white' : 'bg-neutral-100'}`}
              >
                {f.label}
                <span aria-hidden className={`tabular-nums ${filter === f.id ? 'text-white/80' : 'text-neutral-500'}`}>
                  {summary?.[f.countKey] ?? 0}
                </span>
              </button>
            ))}
            <button type="button" className="ml-auto rounded-lg border border-neutral-200 px-3 py-1.5 text-sm" onClick={async () => {
              const blob = await policyDocumentApi.exportAcknowledgements(doc.id);
              triggerBlobDownload(blob, `acknowledgements_${doc.id}.csv`);
            }}>Export report</button>
            <button type="button" disabled={busy} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white" onClick={async () => {
              setBusy(true);
              try {
                const r = await policyDocumentApi.remind(doc.id);
                window.alert(`Reminder sent to ${r.reminded} outstanding employee(s).`);
              } finally {
                setBusy(false);
              }
            }}>Send reminder to outstanding</button>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-neutral-600">
                <tr>
                  <th className="py-2 pr-3">Employee</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Distributed</th>
                  <th className="py-2 pr-3">Opened</th>
                  <th className="py-2 pr-3">Acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {acks.map((row) => (
                  <tr key={row.employeeId} className="border-t border-neutral-100">
                    <td className="py-2 pr-3">{row.employeeName}</td>
                    <td className="py-2 pr-3">{row.location || '—'}</td>
                    <td className="py-2 pr-3">{formatCompanyDate(row.distributedAt)}</td>
                    <td className="py-2 pr-3">{row.openedAt ? formatCompanyDate(row.openedAt) : row.status === 'NotOpened' ? 'Not opened' : '—'}</td>
                    <td className="py-2 pr-3">{row.acknowledgedAt ? formatCompanyDateTime(row.acknowledgedAt) : row.status === 'Opened' ? 'Outstanding' : row.status === 'CoveredByPriorVersion' ? 'Covered' : 'Outstanding'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'versions' && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5 space-y-4">
          {doc.status === 'Published' && (
            <div className="rounded-lg border border-neutral-200 p-4 space-y-3">
              <div className="font-medium">Upload new version</div>
              <input type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={(e) => setNewFile(e.target.files?.[0] || null)} />
              <label className="block text-sm">Version label
                <input value={newVersionLabel} onChange={(e) => setNewVersionLabel(e.target.value)} className="mt-1 rounded-lg border border-neutral-200 px-3 py-2 w-40" />
              </label>
              <div className="text-sm space-y-1">
                <div>Do employees need to acknowledge this version?</div>
                <label className="flex items-center gap-2"><input type="radio" checked={requireNewAck} onChange={() => setRequireNewAck(true)} /> Yes – require new acknowledgement</label>
                <label className="flex items-center gap-2"><input type="radio" checked={!requireNewAck} onChange={() => setRequireNewAck(false)} /> No – retain existing acknowledgement status</label>
              </div>
              <button type="button" disabled={!newFile || busy} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50" onClick={async () => {
                if (!newFile) return;
                setBusy(true);
                try {
                  await policyDocumentApi.uploadFile(doc.id, newFile, { versionLabel: newVersionLabel, requireNewAck });
                  setNewFile(null);
                  await load();
                } finally {
                  setBusy(false);
                }
              }}>Upload new version</button>
            </div>
          )}
          <table className="min-w-full text-sm">
            <thead className="text-left text-neutral-600">
              <tr>
                <th className="py-2">Version</th>
                <th className="py-2">File</th>
                <th className="py-2">Published</th>
                <th className="py-2">Ack</th>
              </tr>
            </thead>
            <tbody>
              {doc.versions.map((v) => (
                <tr key={v.id} className="border-t border-neutral-100">
                  <td className="py-2">v{v.versionLabel}</td>
                  <td className="py-2">{v.fileName || '—'}</td>
                  <td className="py-2">{v.publishedAt ? formatCompanyDateTime(v.publishedAt) : 'Draft'}</td>
                  <td className="py-2">{v.requiresAcknowledgement ? (v.coverPriorAcknowledgements ? 'Covered by prior' : 'Required') : 'Not required'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-neutral-500">{label}</div>
      <div className="font-medium text-neutral-900">{value}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-neutral-50 p-3">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-xs text-neutral-500">{label}</div>
    </div>
  );
}

export default DocumentPolicyDetailPage;
